import { AxisMove, AxisSector, PuzzleDimensions, PuzzleState, TileId } from "./types";

function createSolvedGrid(dimensions: PuzzleDimensions): TileId[][] {
  const { rows, cols } = dimensions;
  const grid: TileId[][] = [];
  let id = 0;
  for (let r = 0; r < rows; r++) {
    const row: TileId[] = [];
    for (let c = 0; c < cols; c++) {
      row.push(id++);
    }
    grid.push(row);
  }
  return grid;
}

export function shuffleGrid(dimensions: PuzzleDimensions, rng: () => number): TileId[][] {
  const flat = createSolvedGrid(dimensions).flat();
  for (let i = flat.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [flat[i], flat[j]] = [flat[j], flat[i]];
  }
  const { rows, cols } = dimensions;
  const grid: TileId[][] = [];
  let idx = 0;
  for (let r = 0; r < rows; r++) {
    const row: TileId[] = [];
    for (let c = 0; c < cols; c++) {
      row.push(flat[idx++]);
    }
    grid.push(row);
  }
  return grid;
}

export function createInitialPuzzle(dimensions: PuzzleDimensions, seed = Date.now()): PuzzleState {
  const rng = mulberry32(seed);
  const grid = shuffleGrid(dimensions, rng);
  // Place axes roughly near the center.
  const verticalIndex = Math.floor(dimensions.cols / 2);
  const horizontalIndex = Math.floor(dimensions.rows / 2);
  return {
    grid,
    dimensions,
    axes: { verticalIndex, horizontalIndex }
  };
}

export function isSolved(state: PuzzleState): boolean {
  const { rows, cols } = state.dimensions;
  let expected = 0;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (state.grid[r][c] !== expected++) {
        return false;
      }
    }
  }
  return true;
}

export function canSwapAxis(state: PuzzleState, axis: "vertical" | "horizontal"): boolean {
  const { verticalIndex, horizontalIndex } = state.axes;
  const { rows, cols } = state.dimensions;
  if (axis === "vertical") {
    // Vertical axis swap requires tiles on both sides (left and right).
    return verticalIndex > 0 && verticalIndex < cols;
  } else {
    // Horizontal axis swap requires tiles on both sides (top and bottom).
    return horizontalIndex > 0 && horizontalIndex < rows;
  }
}

export function applyMove(state: PuzzleState, move: AxisMove): PuzzleState {
  if (move.kind !== "axis-swap") return state;
  
  // Validate that the move is possible.
  if (!canSwapAxis(state, move.axis)) return state;
  
  const copy: TileId[][] = state.grid.map((row) => row.slice());
  const { rows, cols } = state.dimensions;
  const { verticalIndex, horizontalIndex } = state.axes;

  if (move.axis === "vertical") {
    // Swap tiles at (row, verticalIndex-1) and (row, verticalIndex) within selected sector.
    // Determine sector boundaries based on horizontal axis position.
    let rowStart: number, rowEnd: number;
    if (horizontalIndex === 0) {
      // Horizontal axis on top border; only "positive" sector (all rows) is valid.
      if (move.sector === "negative") return state;
      rowStart = 0;
      rowEnd = rows - 1;
    } else if (horizontalIndex === rows) {
      // Horizontal axis on bottom border; only "negative" sector (all rows) is valid.
      if (move.sector === "positive") return state;
      rowStart = 0;
      rowEnd = rows - 1;
    } else {
      // Normal case: horizontal axis splits the grid.
      if (move.sector === "negative") {
        rowStart = 0;
        rowEnd = horizontalIndex - 1;
      } else {
        rowStart = horizontalIndex;
        rowEnd = rows - 1;
      }
    }
    
    for (let r = rowStart; r <= rowEnd; r++) {
      const left = verticalIndex - 1;
      const right = verticalIndex;
      const tmp = copy[r][left];
      copy[r][left] = copy[r][right];
      copy[r][right] = tmp;
    }
  } else {
    // Horizontal axis: swap tiles at (horizontalIndex-1, col) and (horizontalIndex, col)
    // Determine sector boundaries based on vertical axis position.
    let colStart: number, colEnd: number;
    if (verticalIndex === 0) {
      // Vertical axis on left border; only "positive" sector (all cols) is valid.
      if (move.sector === "negative") return state;
      colStart = 0;
      colEnd = cols - 1;
    } else if (verticalIndex === cols) {
      // Vertical axis on right border; only "negative" sector (all cols) is valid.
      if (move.sector === "positive") return state;
      colStart = 0;
      colEnd = cols - 1;
    } else {
      // Normal case: vertical axis splits the grid.
      if (move.sector === "negative") {
        colStart = 0;
        colEnd = verticalIndex - 1;
      } else {
        colStart = verticalIndex;
        colEnd = cols - 1;
      }
    }
    
    for (let c = colStart; c <= colEnd; c++) {
      const top = horizontalIndex - 1;
      const bottom = horizontalIndex;
      const tmp = copy[top][c];
      copy[top][c] = copy[bottom][c];
      copy[bottom][c] = tmp;
    }
  }

  return {
    ...state,
    grid: copy
  };
}

export function moveAxes(
  state: PuzzleState,
  delta: { dVertical?: number; dHorizontal?: number }
): PuzzleState {
  const { rows, cols } = state.dimensions;
  let nextVertical = state.axes.verticalIndex;
  let nextHorizontal = state.axes.horizontalIndex;
  if (typeof delta.dVertical === "number") {
    nextVertical = clamp(nextVertical + delta.dVertical, 0, cols);
  }
  if (typeof delta.dHorizontal === "number") {
    nextHorizontal = clamp(nextHorizontal + delta.dHorizontal, 0, rows);
  }
  
  // Prevent corners: if both axes would be on borders, adjust to nearest non-corner.
  const isVerticalOnBorder = nextVertical === 0 || nextVertical === cols;
  const isHorizontalOnBorder = nextHorizontal === 0 || nextHorizontal === rows;
  
  if (isVerticalOnBorder && isHorizontalOnBorder) {
    // Would create a corner. Move at least one axis off the border.
    // Strategy: move the axis that changed less (or revert completely if neither changed).
    const verticalDelta = Math.abs(nextVertical - state.axes.verticalIndex);
    const horizontalDelta = Math.abs(nextHorizontal - state.axes.horizontalIndex);
    
    if (verticalDelta === 0 && horizontalDelta === 0) {
      // No change requested, keep current position.
      return state;
    } else if (verticalDelta <= horizontalDelta) {
      // Move vertical axis off border (by 1 step toward center).
      if (nextVertical === 0) {
        nextVertical = 1;
      } else if (nextVertical === cols) {
        nextVertical = cols - 1;
      }
    } else {
      // Move horizontal axis off border (by 1 step toward center).
      if (nextHorizontal === 0) {
        nextHorizontal = 1;
      } else if (nextHorizontal === rows) {
        nextHorizontal = rows - 1;
      }
    }
  }
  
  return {
    ...state,
    axes: {
      verticalIndex: nextVertical,
      horizontalIndex: nextHorizontal
    }
  };
}

export function sectorLabelForAxis(axis: "vertical" | "horizontal", sector: AxisSector): string {
  if (axis === "vertical") {
    return sector === "negative" ? "Above axis" : "Below axis";
  }
  return sector === "negative" ? "Left of axis" : "Right of axis";
}

function clamp(v: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, v));
}

// Deterministic PRNG for reproducible shuffles.
function mulberry32(a: number): () => number {
  return function () {
    let t = (a += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}


